import { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Download, WifiOff } from 'lucide-react';
import type { Lab } from '../lib/lab';
import { isWellFormed, lookupLab, storeLab } from '../lib/lab';
import { isNativeShell } from '../lib/nativeBridge';
import { Button, cx } from './ui';

const LENGTH = 8;

/**
 * Join an existing lab. Eight digits name it.
 *
 * This is reached on purpose from the sign-in screen, never forced. It used to
 * stand in front of the whole app, which is what made the app unreachable for
 * anyone without a code; see the note in App.tsx.
 *
 * Deliberately says nothing about which codes exist — a wrong code and a
 * non-existent code give the same message.
 */
export default function LabGate({
  onUnlocked,
  onCancel,
}: {
  onUnlocked: (lab: Lab) => void;
  onCancel: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(''));
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const code = digits.join('');
  const complete = isWellFormed(code);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  function setDigit(index: number, value: string) {
    const clean = value.replace(/\D/g, '');
    setError(null);

    if (clean.length > 1) {
      // Pasted or fast-typed run of digits: spread it across the boxes.
      const next = [...digits];
      for (let i = 0; i < clean.length && index + i < LENGTH; i++) {
        next[index + i] = clean[i];
      }
      setDigits(next);
      inputs.current[Math.min(index + clean.length, LENGTH - 1)]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = clean;
    setDigits(next);
    if (clean && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  }

  function onKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  }

  async function submit() {
    if (!complete || checking) return;
    setChecking(true);
    setError(null);

    const result = await lookupLab(code);
    setChecking(false);

    if (result.ok) {
      storeLab(result.lab);
      onUnlocked(result.lab);
      return;
    }

    if (result.reason === 'offline') {
      setError('No connection. The lab code has to be checked once before you can sign in.');
      return;
    }
    if (result.reason === 'inactive') {
      setError('That lab code is no longer active. Ask your lab admin.');
      return;
    }
    setError('That code was not recognised. Check the digits and try again.');
    setDigits(Array(LENGTH).fill(''));
    inputs.current[0]?.focus();
  }

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-6">
      <div className="w-full max-w-md">
        <div className="mb-5 text-center">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Blisx<span className="text-accent-400">Bench</span>
          </h1>
          <p className="mt-1.5 text-sm text-ink-400">
            Enter the eight-digit code your lab administrator sent you.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="mb-2 flex w-full items-center justify-center gap-1 sm:gap-2">
            {digits.map((d, i) => (
              <div key={i} className="contents">
                <input
                  ref={(el) => {
                    inputs.current[i] = el;
                  }}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={LENGTH}
                  aria-label={`Digit ${i + 1} of ${LENGTH}`}
                  disabled={checking}
                  className={cx(
                    'h-12 min-w-0 flex-1 rounded-lg border bg-ink-800 text-center text-lg font-semibold tabular-nums text-ink-100',
                    'max-w-12 sm:text-xl',
                    'focus:outline-none focus:ring-2 focus:ring-accent-500',
                    error ? 'border-red-700' : 'border-ink-600 focus:border-accent-500',
                    'disabled:opacity-50',
                  )}
                />
                {/* Grouped 4+4 — eight undifferentiated boxes are hard to read back. */}
                {i === 3 && <span className="shrink-0 px-0.5 text-ink-600">–</span>}
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 flex gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
              {error.startsWith('No connection') ? (
                <WifiOff size={16} className="mt-0.5 shrink-0" />
              ) : (
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
              )}
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="mt-4 w-full"
            loading={checking}
            disabled={!complete}
            icon={!checking ? <ArrowRight size={18} /> : undefined}
          >
            Continue
          </Button>
        </form>

        <button
          onClick={onCancel}
          className="mt-4 flex w-full items-center justify-center gap-1.5 text-sm text-ink-400 underline-offset-2 hover:text-ink-100 hover:underline"
        >
          <ArrowLeft size={15} />
          Back to sign in
        </button>

        <p className="mt-4 text-center text-xs text-ink-400">
          No code? You do not need one. Sign in and the app keeps your records under your own
          account.
        </p>

        {/* Daily users are signed in already, so the app stays the landing page;
            this is here for the person arriving without it installed. */}
        {!isNativeShell() && (
          <a
            href="/download"
            className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-ink-700 py-2.5 text-sm text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100"
          >
            <Download size={15} />
            Download App
          </a>
        )}

        <p className="mt-3 flex justify-center gap-4 text-xs text-ink-400">
          <a href="/privacy" className="hover:text-ink-100">Privacy</a>
          <a href="/terms" className="hover:text-ink-100">Terms</a>
          <a href="/support" className="hover:text-ink-100">Support</a>
        </p>
      </div>
    </div>
  );
}
